import { oklchStops } from "../color";

import { definePreset, DEGREES_SCHEMA, kf, timing } from "./helpers";

import type { AnimationPreset, ParamSchema } from "./types";

/** Colors are persisted as numeric hex (params are numbers-only); the panel
 * renders params keyed "color" as a color picker. */
const COLOR_SCHEMA: ParamSchema = {
  key: "color",
  label: "Color",
  min: 0,
  max: 0xffffff,
  step: 1,
};

export const paramToColor = (value: number | undefined, fallback: number) =>
  `#${Math.max(0, Math.min(0xffffff, Math.round(value ?? fallback)))
    .toString(16)
    .padStart(6, "0")}`;

const RAINBOW_STOP_COUNT = 7;
const RAINBOW_DEFAULTS = Array.from(
  { length: RAINBOW_STOP_COUNT },
  (_, i) => `hsl(${(i * 60) % 360}, 85%, 55%)`,
);
const rainbowHexDefault = (i: number) => {
  // convert the default HSL string to a hex int once, for the numeric
  // params-only param encoding (see COLOR_SCHEMA's doc-comment above)
  const [h, s, l] = RAINBOW_DEFAULTS[i].match(/\d+/g)!.map(Number);
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l / 100 - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return parseInt(`${f(0)}${f(8)}${f(4)}`, 16);
};
const RAINBOW_SCHEMAS: ParamSchema[] = Array.from(
  { length: RAINBOW_STOP_COUNT },
  (_, i) => ({
    key: `rainbowColor${i}`,
    label: `Hue ${i + 1}`,
    min: 0,
    max: 0xffffff,
    step: 1,
  }),
);

/** ponytail: animates the first MAX_ANIMATED_GRADIENT_STOPS stops; extra
 * authored stops beyond that keep their static color. Raise this if a
 * design needs more animated stops. */
const MAX_ANIMATED_GRADIENT_STOPS = 4;
const GRADIENT_COLOR_SCHEMAS: ParamSchema[] = Array.from(
  { length: MAX_ANIMATED_GRADIENT_STOPS },
  (_, i) => ({
    key: `gradientColor${i}`,
    label: `Stop ${i + 1} Color`,
    min: 0,
    max: 0xffffff,
    step: 1,
  }),
);

export const colorPresets: AnimationPreset[] = [
  definePreset(
    "strokeTint",
    "Stroke Tint",
    "color",
    { params: { color: 0xe03131 }, schema: [COLOR_SCHEMA] },
    (tl, target, element, cfg) => {
      // OKLCH stops: GSAP lerps sRGB between close neighbours, which tracks
      // the perceptual gradient instead of the muddy direct sRGB midpoint
      tl.to(
        target,
        {
          keyframes: {
            strokeColor: oklchStops(
              element.strokeColor,
              paramToColor(cfg.params.color, 0xe03131),
            ),
            easeEach: "none",
          },
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "fillTint",
    "Fill Tint",
    "color",
    { params: { color: 0xa5d8ff }, schema: [COLOR_SCHEMA] },
    (tl, target, element, cfg) => {
      // only visible on elements with a background fill; "transparent" fades
      // in from alpha-0 of the TARGET hue (oklchStops handles it)
      tl.to(
        target,
        {
          keyframes: {
            backgroundColor: oklchStops(
              element.backgroundColor,
              paramToColor(cfg.params.color, 0xa5d8ff),
            ),
            easeEach: "none",
          },
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "rainbow",
    "Rainbow",
    "color",
    {
      duration: 2000,
      easing: "none",
      params: Object.fromEntries(
        Array.from({ length: RAINBOW_STOP_COUNT }, (_, i) => [
          `rainbowColor${i}`,
          rainbowHexDefault(i),
        ]),
      ),
      schema: RAINBOW_SCHEMAS,
    },
    (tl, target, element, cfg) => {
      const stops = Array.from({ length: RAINBOW_STOP_COUNT }, (_, i) =>
        paramToColor(cfg.params[`rainbowColor${i}`], rainbowHexDefault(i)),
      );
      tl.to(
        target,
        {
          keyframes: {
            strokeColor: [element.strokeColor, ...stops, element.strokeColor],
            easeEach: "none",
          },
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "colorBlink",
    "Color Blink",
    "color",
    { params: { color: 0xe03131 }, schema: [COLOR_SCHEMA] },
    (tl, target, element, cfg) => {
      const to = paramToColor(cfg.params.color, 0xe03131);
      const from = element.strokeColor;
      tl.to(
        target,
        {
          keyframes: {
            strokeColor: [from, to, from, to, from],
            easeEach: "none",
          },
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "hueCycle",
    "Hue Cycle",
    "color",
    { params: { degrees: 360 }, schema: [DEGREES_SCHEMA], easing: "none" },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { hueRotate: 0 },
        { hueRotate: cfg.params.degrees ?? 360, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "brightnessPulse",
    "Brightness",
    "color",
    {
      params: { brightness: 1.6 },
      schema: [
        { key: "brightness", label: "Brightness", min: 0, max: 4, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        kf(
          cfg,
          { brightness: [1, cfg.params.brightness ?? 1.6, 1] },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "desaturate",
    "Desaturate",
    "color",
    {
      params: { to: 0 },
      schema: [{ key: "to", label: "Saturation", min: 0, max: 4, step: 0.1 }],
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { saturate: 1 },
        { saturate: cfg.params.to ?? 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "grayscaleIn",
    "Grayscale",
    "color",
    {
      params: { to: 1 },
      schema: [{ key: "to", label: "Amount", min: 0, max: 1, step: 0.1 }],
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { grayscale: 0 },
        { grayscale: cfg.params.to ?? 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "invertFlash",
    "Invert Flash",
    "color",
    {
      params: { flashes: 2 },
      schema: [{ key: "flashes", label: "Flashes", min: 1, max: 10, step: 1 }],
    },
    (tl, target, _element, cfg) => {
      const flashes = Math.max(1, Math.round(cfg.params.flashes ?? 2));
      const invert = [0];
      for (let i = 0; i < flashes; i++) {
        invert.push(1, 0);
      }
      tl.to(target, kf(cfg, { invert }, "none"), 0);
    },
  ),
  definePreset(
    "glow",
    "Glow",
    "color",
    {
      params: { blur: 4, brightness: 1.5 },
      schema: [
        { key: "blur", label: "Blur px", min: 0, max: 30, step: 1 },
        { key: "brightness", label: "Brightness", min: 1, max: 4, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        kf(
          cfg,
          {
            blur: [0, cfg.params.blur ?? 4, 0],
            brightness: [1, cfg.params.brightness ?? 1.5, 1],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "gradientRotate",
    "Gradient Rotate",
    "color",
    {
      params: { rotations: 1 },
      schema: [
        { key: "rotations", label: "Rotations", min: -10, max: 10, step: 0.5 },
      ],
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { gradientAngleDelta: 0 },
        {
          gradientAngleDelta: (cfg.params.rotations ?? 1) * 360,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "gradientMarch",
    "Gradient March",
    "color",
    {
      params: { loops: 1 },
      schema: [{ key: "loops", label: "Loops", min: -10, max: 10, step: 0.5 }],
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { gradientOffsetShift: 0 },
        { gradientOffsetShift: cfg.params.loops ?? 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "gradientColors",
    "Gradient Colors",
    "color",
    {
      params: {
        gradientColor0: 0xffffff,
        gradientColor1: 0x4dabf7,
        gradientColor2: 0xffd43b,
        gradientColor3: 0xe03131,
      },
      schema: GRADIENT_COLOR_SCHEMAS,
    },
    (tl, target, element, cfg) => {
      const stops = element.strokeGradient?.stops ?? [];
      if (!stops.length) {
        return;
      }
      // seeded up front so the array exists (and reads correctly) before any
      // tween renders, and so stops past the animated window keep their color
      const stopColors = stops.map((s) => s.color);
      target.gradientStopColors = stopColors;
      stops.slice(0, MAX_ANIMATED_GRADIENT_STOPS).forEach((stop, i) => {
        // ponytail: GSAP reads a raw array target as a selector list, so each
        // stop tweens its own one-key proxy. GSAP does the color interpolation
        // itself (hex, 8-digit hex and rgba() all parse) — same machinery as
        // strokeTint, no bespoke lerp to keep in sync with what oklchStops
        // emits. The copy into stopColors happens in a property SETTER, not
        // an onUpdate callback: sampleAt() seeks with suppressed callbacks
        // (the runtime contract is "tweens write directly into targets"), and
        // an onUpdate copy would leave scrubbing and export frozen at the
        // authored colors.
        const proxy = {
          _color: stop.color,
          get color() {
            return this._color;
          },
          set color(value: string) {
            this._color = value;
            stopColors[i] = value;
          },
        };
        tl.to(
          proxy,
          {
            keyframes: {
              color: oklchStops(
                stop.color,
                paramToColor(cfg.params[`gradientColor${i}`], 0xffffff),
              ),
              easeEach: "none",
            },
            ...timing(cfg),
          },
          0,
        );
      });
    },
  ),
];
